"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Search, ArrowRight } from "lucide-react";
import type { SearchDoc, SearchType } from "@/lib/search";

const TYPE_LABEL: Record<SearchType, string> = {
  Course: "Course",
  Certification: "Certification",
  Blog: "Article",
  Page: "Page",
};

const TYPE_ORDER: SearchType[] = ["Course", "Certification", "Blog", "Page"];

/** Ranks docs against a query: title hits weigh most, then keywords, then
 *  description. Every whitespace-separated token must match somewhere (AND). */
function rank(docs: SearchDoc[], query: string): SearchDoc[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/);
  return docs
    .map((doc) => {
      const title = doc.title.toLowerCase();
      const keywords = doc.keywords.join(" ").toLowerCase();
      const description = doc.description.toLowerCase();
      let score = 0;
      const every = tokens.every(
        (t) => title.includes(t) || keywords.includes(t) || description.includes(t),
      );
      if (!every) return { doc, score: 0 };
      for (const t of tokens) {
        if (title.includes(t)) score += 3;
        if (keywords.includes(t)) score += 2;
        if (description.includes(t)) score += 1;
      }
      return { doc, score };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((s) => s.doc);
}

export function SearchClient({ docs }: { docs: SearchDoc[] }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Keep the URL shareable, debounced so we don't push history on every key.
  useEffect(() => {
    const id = setTimeout(() => {
      const q = query.trim();
      router.replace(q ? `/search?q=${encodeURIComponent(q)}` : "/search", { scroll: false });
    }, 300);
    return () => clearTimeout(id);
  }, [query, router]);

  const results = useMemo(() => rank(docs, query), [docs, query]);
  const grouped = useMemo(() => {
    const map = new Map<SearchType, SearchDoc[]>();
    for (const doc of results) {
      const list = map.get(doc.type) ?? [];
      list.push(doc);
      map.set(doc.type, list);
    }
    return TYPE_ORDER.filter((t) => map.has(t)).map((t) => [t, map.get(t)!] as const);
  }, [results]);

  const trimmed = query.trim();

  return (
    <div>
      {/* Search input */}
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-cb-muted-2"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="search"
          inputMode="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search courses, certifications, labs, and articles"
          aria-label="Search the site"
          className="w-full rounded-xl border border-cb-border bg-cb-surface py-4 pl-12 pr-4 text-base text-cb-text placeholder:text-cb-muted-2 focus-visible:border-cb-violet/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/40"
        />
      </div>

      {/* Result count */}
      {trimmed && (
        <p className="mt-4 text-sm text-cb-muted-2" aria-live="polite">
          {results.length} {results.length === 1 ? "result" : "results"} for &ldquo;{trimmed}&rdquo;
        </p>
      )}

      {/* Empty prompt */}
      {!trimmed && (
        <p className="mt-6 text-sm text-cb-muted">
          Start typing to search across courses, certifications, labs, articles, and pages.
        </p>
      )}

      {/* No results */}
      {trimmed && results.length === 0 && (
        <div className="mt-6 rounded-xl border border-cb-border bg-cb-surface p-6">
          <p className="text-sm text-cb-muted">
            No matches. Try a broader term such as{" "}
            <button
              type="button"
              onClick={() => setQuery("azure")}
              className="text-cb-violet-text hover:underline"
            >
              azure
            </button>
            ,{" "}
            <button
              type="button"
              onClick={() => setQuery("certification")}
              className="text-cb-violet-text hover:underline"
            >
              certification
            </button>
            , or{" "}
            <button
              type="button"
              onClick={() => setQuery("labs")}
              className="text-cb-violet-text hover:underline"
            >
              labs
            </button>
            .
          </p>
        </div>
      )}

      {/* Results, grouped by type */}
      {grouped.map(([type, items]) => (
        <section key={type} className="mt-8">
          <p className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-widest text-cb-violet-text">
            {TYPE_LABEL[type]}
            <span className="ml-2 text-cb-muted-2">{items.length}</span>
          </p>
          <ul className="space-y-2">
            {items.map((doc) => (
              <li key={`${doc.type}-${doc.url}-${doc.title}`}>
                <Link
                  href={doc.url}
                  className="group flex items-start gap-3 rounded-xl border border-cb-border bg-cb-surface p-4 transition-colors hover:border-cb-violet/40 hover:bg-cb-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/40"
                >
                  <div className="min-w-0 flex-1">
                    <h2 className="text-sm font-semibold text-cb-text group-hover:text-white">
                      {doc.title}
                    </h2>
                    <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-cb-muted">
                      {doc.description}
                    </p>
                  </div>
                  <ArrowRight
                    className="mt-1 h-4 w-4 shrink-0 text-cb-muted-2 transition-transform group-hover:translate-x-0.5 group-hover:text-cb-violet-text"
                    aria-hidden="true"
                  />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
